import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@material-ui/core";
import { CheckCircle, Warning } from "@material-ui/icons";
import { getFullName } from "utils/Haim/utils";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { NewContactPicker } from "components/_dashboard/listingsMatch/NewContactPicker";
import type { RealEstateContact } from "../../../../@types/sharedSchema";
import { isVirtualPhone } from "./sellerPhone";
import {
  applyPickedContactToDraft,
  clearPickedContact,
  sellerDraftAsPickerContact
} from "./listingSellerDraft";
import type { ListingSellerDraft, PickedContactLike } from "./listingSellerDraft";
import useContactByPhone from "hooks/contacts/useContactByPhone";

/**
 * The seller of the property, on the listing form.
 *
 * ## One question at a time
 *
 * There are exactly two ways to name an owner, and they are alternatives, not
 * layers: she is ALREADY in the realtor's book, or she is not. The first version
 * of this section showed both routes at once — a contacts picker, three free-text
 * fields, and a "create contact" button, stacked — and was rejected on sight:
 * three controls for one answer, with no indication which one you were meant to
 * use or what the other two would then do with your input.
 *
 * So the mode is an explicit choice, and only that mode's control is mounted:
 *
 *   ┌ מאנשי הקשר  → the picker.   No name/phone fields: the contact card IS the
 *   │                             identity, and a second editable copy of it on
 *   │                             screen only invites the two to disagree.
 *   └ הזנה ידנית  → the fields.   No picker: she has already said this person is
 *                                 not one she is choosing from a list.
 *
 * ## Creating a CRM contact is a decision, not a side effect
 *
 * Recording who the owner is and adding a person to the CRM are different acts.
 * A realtor mid-qualification-call wants the first and often not the second — the
 * number may be a Yad2 mask, the "owner" may turn out to be a tenant, the deal may
 * die on the call. Previously any typed phone silently minted a contact.
 *
 * Now it is an opt-in switch, and it appears ONLY where it is a real choice: in
 * manual mode, with a phone typed, that does not already belong to a contact. When
 * the number IS already a contact there is nothing to decide — the listing links
 * to that person either way, which is a link, not a creation.
 *
 * Either way `listingContacts[0]` is written from what is on screen, so the owner
 * is recorded on the property whether or not a CRM card exists.
 *
 * ## Two schemas meet here, and the phone is called something different in each
 *
 * A CONTACT's phone IS its Firestore document id. A LISTING stores the seller's
 * phone in the `callerPhoneNumber` FIELD. Every crossing goes through the explicit
 * adapters in `listingSellerDraft.ts` (`sellerDraftAsPickerContact` /
 * `applyPickedContactToDraft`) — never a cast. Skipping that adapter is precisely
 * how the old picker's chips lost their numbers and how `isOptionEqualToValue`
 * started matching a saved seller against every option.
 *
 * ## The status chip is keyed on the PHONE, always
 *
 * Not on whether a contact was picked. The phone is the ground truth — it is the
 * document id — so the same lookup answers correctly for a book pick and for a
 * number typed off a listing ad. `undefined` from `useContactByPhone` means
 * STILL RESOLVING and is rendered as nothing: showing the create switch before the
 * answer arrives is how a fast click mints a duplicate of a contact we were about
 * to find.
 *
 * ## The virtual-number warning is not cosmetic
 *
 * Most yad2 seller numbers are Yad2 masks (073/055), and a contact's document id
 * IS its phone. Saving a mask mints a contact nobody can ever reach, repairable
 * only through the phone re-key flow. So the warning is loud, sits directly under
 * the field, and does NOT block the save: she is one keystroke from the real
 * number, and a blocked save is worse than a contact she can re-key.
 */

type SellerMode = "pick" | "manual";

type ListingSellerSectionProps = {
  value: ListingSellerDraft;
  onChange: (next: ListingSellerDraft) => void;
  loading?: boolean;
  /** Drives the wording only: a rental listing's owner is a משכיר, not a מוכר. */
  transactionType?: "sale" | "rent" | null;
};

/**
 * Re-exported so callers keep a single import. The implementations live in
 * `listingSellerDraft.ts`, which has no firebase/DOM/hook imports — see that file
 * for why.
 */
export {
  sellerDraftFromListingContacts,
  listingContactsFromSellerDraft
} from "./listingSellerDraft";
export type { ListingSellerDraft } from "./listingSellerDraft";

/** Enough digits to be a phone at all — below this, no snapshot per keystroke. */
const MIN_LOOKUP_DIGITS = 7;

/**
 * Which mode to open in, from what is already stored.
 *
 * A seller that resolved to a contact opens in `pick` — that is what it IS, and
 * showing it as free text would invite an edit that silently unlinks it. Anything
 * else with details typed on it opens in `manual`, which covers the common claim
 * case where the fields arrive pre-filled from the scraped ad. An empty section
 * opens in `pick`, because "do I already know this person?" is the cheaper
 * question to answer first.
 */
const initialMode = (draft: ListingSellerDraft): SellerMode => {
  if (draft.contactId) return "pick";
  const typed = draft.firstName.trim() || draft.lastName.trim() || draft.phone.trim();
  return typed ? "manual" : "pick";
};

export function ListingSellerSection({
  value,
  onChange,
  loading,
  transactionType
}: ListingSellerSectionProps): JSX.Element {
  const { t: translate } = useTranslation();
  const [mode, setMode] = useState<SellerMode>(() => initialMode(value));
  /**
   * What a pick discarded, so it can be put back.
   *
   * Picking a contact REPLACES the typed name and phone — see the rule in
   * `applyPickedContactToDraft`. That is right, but it must never be silent, and
   * `applyPickedContactToDraft` only reports a discard when there was actually
   * something different to lose. So in the ordinary "pick first, then done" case
   * this stays null and nothing is shown.
   */
  const [replacedByPick, setReplacedByPick] = useState<
    Pick<ListingSellerDraft, "firstName" | "lastName" | "phone"> | null
  >(null);

  const phone = value.phone.trim();
  const lookupEnabled = phone.replace(/\D/g, "").length >= MIN_LOOKUP_DIGITS;
  const { exists: isExistingContact, contact: contactData } = useContactByPhone(
    lookupEnabled ? phone : undefined,
    lookupEnabled
  );

  const virtual = useMemo(() => isVirtualPhone(phone), [phone]);
  const roleLabel = translate(
    transactionType === "rent"
      ? "listingsPage.sellerSection.landlord"
      : "listingsPage.sellerSection.seller"
  );

  const pickerValue = useMemo(() => sellerDraftAsPickerContact(value), [value]);
  const extraContactsCount = value.extraContacts?.length ?? 0;

  /**
   * Editing the phone drops any `contactId` carried over from storage or a pick.
   *
   * That id was resolved for a DIFFERENT number, so keeping it would quietly link
   * the listing to the wrong person. It is not re-derived here: this component
   * never resolves the id it saves — `persistListingSeller` does that at save
   * time, from the phone as finally typed.
   *
   * It also drops `createContact`: the switch's answer was about a different
   * number, and silently carrying "yes, create" onto a newly typed one is how a
   * corrected mask would still mint the contact she had just decided against.
   *
   * Editing the NAME keeps both. The listing's typed name is the LISTING's own
   * record — `persistListingSeller` never writes it back onto the contact — so
   * correcting a spelling is not a claim that this is a different person.
   */
  const handlePhoneChange = (nextPhone: string): void => {
    setReplacedByPick(null);
    const { createContact, ...rest } = value;
    onChange(clearPickedContact({ ...rest, phone: nextPhone }));
  };

  /**
   * A contact chosen in the picker, or `null` when she cleared it.
   *
   * Clearing keeps the typed name and number and only drops the link — see
   * `clearPickedContact`. `parseNewContact` inside the picker can also hand back
   * `null` for an unparseable freeSolo entry, which lands here identically and
   * correctly: nothing was chosen.
   */
  const handlePicked = useCallback(
    (contact: RealEstateContact | null): void => {
      if (!contact) {
        setReplacedByPick(null);
        onChange(clearPickedContact(value));
        return;
      }
      const { next, replaced } = applyPickedContactToDraft(value, contact as PickedContactLike);
      setReplacedByPick(replaced ?? null);
      onChange(next);
    },
    [onChange, value]
  );

  /**
   * Switching mode changes which QUESTION is being asked, so it must not silently
   * change the ANSWER.
   *
   * Leaving `pick` keeps the picked person's name and number as the starting text
   * — she is about to edit them, and re-typing what was on screen a moment ago is
   * pure friction — but drops the `contactId`, because free text that still claims
   * to be a specific contact document is the mismatch this whole section exists to
   * avoid. Leaving `manual` keeps the typed details; the picker simply shows
   * nothing until she chooses someone.
   */
  const handleModeChange = (next: SellerMode | null): void => {
    if (!next || next === mode) return;
    setReplacedByPick(null);
    if (next === "manual" && value.contactId) onChange(clearPickedContact(value));
    setMode(next);
  };

  const undoPick = (): void => {
    if (!replacedByPick) return;
    onChange(clearPickedContact({ ...value, ...replacedByPick }));
    setReplacedByPick(null);
  };

  /**
   * The create switch is only a question when creating is actually on the table:
   * manual entry, a phone long enough to key a document by, and a resolved answer
   * of "not in the book". While the lookup is still `undefined` it stays hidden —
   * see the note on the status chip above.
   */
  const canOfferCreate = mode === "manual" && lookupEnabled && isExistingContact === false;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            {roleLabel}
          </Typography>
          {/* `disabled` sits on each button, not the group: this MUI alpha's
              ToggleButtonGroup does not accept it. */}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_event, next: SellerMode | null) => handleModeChange(next)}
          >
            <ToggleButton value="pick" disabled={loading}>
              {translate("listingsPage.sellerSection.modePick")}
            </ToggleButton>
            <ToggleButton value="manual" disabled={loading}>
              {translate("listingsPage.sellerSection.modeManual")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Grid>

      {mode === "pick" ? (
        <Grid item xs={12}>
          {/* The picker asks the server only once its menu is opened, so mounting this
              card — one optional part of a large form — costs nothing. */}
          <NewContactPicker
            loading={loading}
            label={translate("listingsPage.sellerSection.pickFromContacts")}
            selectedContact={(pickerValue as RealEstateContact | null) ?? null}
            setSelectedContact={handlePicked}
          />
        </Grid>
      ) : (
        <>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              disabled={loading}
              value={value.firstName}
              label={translate("listingsPage.sellerSection.firstName")}
              onChange={(event) => onChange({ ...value, firstName: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              disabled={loading}
              value={value.lastName}
              label={translate("listingsPage.sellerSection.lastName")}
              onChange={(event) => onChange({ ...value, lastName: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              disabled={loading}
              value={value.phone}
              label={translate("listingsPage.sellerSection.phone")}
              onChange={(event) => handlePhoneChange(event.target.value)}
              error={virtual}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Stack spacing={1}>
          {replacedByPick && (
            <Alert
              severity="info"
              action={
                <Button color="inherit" size="small" onClick={undoPick}>
                  {translate("listingsPage.sellerSection.undoPick")}
                </Button>
              }
            >
              {translate("listingsPage.sellerSection.pickReplacedTypedDetails")}
            </Alert>
          )}

          {virtual && (
            <Alert severity="warning" icon={<Warning fontSize="inherit" />}>
              {translate("listingsPage.sellerSection.virtualNumberWarning")}
            </Alert>
          )}

          {/* Shown in BOTH modes: in `pick` it confirms who was chosen, and in
              `manual` it is the answer to "is this number already someone I know?"
              — in which case the listing links to them and the create switch below
              is correctly absent. */}
          {lookupEnabled && isExistingContact === true && contactData && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<CheckCircle />}
              sx={{ alignSelf: "flex-start" }}
              label={translate("listingsPage.sellerSection.existingContact", {
                name: contactDisplayName(contactData)
              })}
            />
          )}

          {canOfferCreate && (
            <FormControlLabel
              sx={{ alignSelf: "flex-start", mr: 0 }}
              control={
                <Switch
                  size="small"
                  disabled={loading}
                  checked={Boolean(value.createContact)}
                  onChange={(event) => onChange({ ...value, createContact: event.target.checked })}
                />
              }
              label={
                <Stack>
                  <Typography variant="body2">
                    {translate("listingsPage.sellerSection.saveAsContact", { role: roleLabel })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {translate("listingsPage.sellerSection.saveAsContactHint")}
                  </Typography>
                </Stack>
              }
            />
          )}

          {/* Said out loud rather than assumed. The form edits ONE seller, but the
              field is an array that a MULTI picker wrote for years, so a listing
              can genuinely hold co-owners. They survive this save untouched
              (`extraContacts`) — and a realtor who cannot see them has no way to
              know that. */}
          {extraContactsCount > 0 && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {translate("listingsPage.sellerSection.extraContactsKept", {
                count: extraContactsCount
              })}
            </Typography>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
}

/**
 * What to call a contact we found by phone.
 *
 * ⚠ THE FALLBACK IS `id`, NOT `callerPhoneNumber`. `postProcessContact` stopped
 * stamping `callerPhoneNumber` (upstream `1ffe674b`), so it is absent on ~90% of
 * contact documents and disagrees with the id on some of the rest — reading it
 * here rendered a literal "מקושר לאיש קשר קיים: undefined". The document id IS
 * the phone; nothing else is.
 */
function contactDisplayName(contact: RealEstateContact): string {
  const name = getFullName(contact.callerFirstName, contact.callerLastName);
  if (name) return name;
  const id = contact.id ?? "";
  return parsePhoneNumber(id)?.formatNational() ?? id;
}
